import { pathToUrl } from '../../utils/router';
import { PageRoutesEnum } from '../../utils/routes';

import {
  BudgetStatus,
  CandidateContext,
  CategoryName,
  MatchingSource,
  Processing,
  Recommendation,
  Recommendations,
  StageStatus,
} from './matching.types';

export const budgetLabels: Record<BudgetStatus, string> = {
  within_budget: 'Within budget',
  over_budget: 'Over budget',
  unknown: 'Budget unknown',
};
export const categoryLabels: Record<CategoryName, string> = {
  skills: 'Skills',
  experience: 'Experience',
  roleAlignment: 'Role alignment',
  languages: 'Languages',
  salary: 'Salary',
  location: 'Location',
};
export const stageLabels: Record<StageStatus, string> = {
  PENDING: 'Queued',
  PROCESSING: 'Processing',
  READY: 'Ready',
  RETRY_PENDING: 'Retry queued',
  FAILED_RETRYABLE: 'Failed; awaiting recovery',
  FAILED_NON_RETRYABLE: 'Failed',
};
export const candidateName = (candidate: Pick<CandidateContext, 'firstName' | 'lastName'>) =>
  [candidate.firstName, candidate.lastName].filter(Boolean).join(' ') || 'Unnamed candidate';
export const counterpartName = (row: Recommendation) =>
  'candidate' in row ? candidateName(row.candidate) : row.project.name || 'Unnamed project';
export const counterpartUuid = (row: Recommendation) => ('candidate' in row ? row.candidate.uuid : row.project.uuid);
export const sourceRecordPath = (source: MatchingSource) =>
  pathToUrl(source.type === 'candidate' ? PageRoutesEnum.candidateEdit : PageRoutesEnum.projectsEdit, {
    id: source.uuid,
  });
export const matchingListPath = (source: MatchingSource) =>
  pathToUrl(source.type === 'candidate' ? PageRoutesEnum.candidateMatches : PageRoutesEnum.projectMatches, {
    id: source.uuid,
  });
export const matchingDetailPath = (source: MatchingSource, matchId: string) =>
  pathToUrl(source.type === 'candidate' ? PageRoutesEnum.candidateMatchDetails : PageRoutesEnum.projectMatchDetails, {
    id: source.uuid,
    matchId,
  });
export const processingReason = (reason: string) => {
  const labels: Record<string, string> = {
    MATCHING_CONFIG_MISMATCH:
      'Matching service configuration does not match the backend. Check environment configuration.',
    MATCHING_NOT_CONFIGURED: 'The matching service is not configured. Check environment configuration and logs.',
    MATCHING_DISABLED: 'The matching service is disabled. Check its environment configuration.',
    'processing-disabled': 'Processing is disabled',
    'assessment-version-unverified': 'Assessment version could not be verified',
    'outside-automatic-scope': 'Outside automatic matching dates',
    'candidate-anonymized': 'Candidate data has been anonymized',
    'missing-data-processing-consent': 'Data Processing and Offers consent is required',
  };
  return labels[reason] || reason.replace(/[-_]/g, ' ').toLowerCase();
};
const stageStatuses = (processing: Processing) => [processing.quickStatus, processing.detailedStatus];
// A blocked pair is not auto-updated whatever its stage status; only unblocked failures count as failed.
export const isUpdateFailed = (processing: Processing) =>
  !processing.blockedReason && stageStatuses(processing).some((status) => status?.startsWith('FAILED'));
export const updatingLabel = (processing: Processing) =>
  processing.refreshPending ? (stageStatuses(processing).includes('PROCESSING') ? 'Updating' : 'Update queued') : null;
// Matches the admin view, where a failed stage of a blocked pair counts as Blocked.
export const stageLabel = (status: StageStatus | null, processing: Processing) =>
  !status ? 'Not scheduled' : processing.blockedReason && status.startsWith('FAILED') ? 'Blocked' : stageLabels[status];
export const blockedExplanation = (reason: string) =>
  reason === 'outside-automatic-scope'
    ? "It won't be updated automatically because this pair is outside the automatic matching dates."
    : `${processingReason(reason)}. It won't be updated automatically.`;
export type PageWideState = 'stale' | 'notAutoUpdated' | 'budgetUnknown';
// Computed from the current page only, so the banner never describes rows the recruiter cannot see.
export const pageWideStates = (rows: Recommendation[]) => {
  const states: Record<PageWideState, (row: Recommendation) => boolean> = {
    stale: (row) => row.freshness.stale,
    notAutoUpdated: (row) => !!row.processing.blockedReason,
    budgetUnknown: (row) => row.budgetStatus === 'unknown',
  };
  return new Set(
    (Object.keys(states) as PageWideState[]).filter((state) => rows.length > 0 && rows.every(states[state]))
  );
};
// Row tags, minus states the page-wide banner already states.
export const visibleRowFlags = (row: Recommendation, pageWide: Set<PageWideState>) => ({
  outdated: row.freshness.stale && !pageWide.has('stale'),
  versionUnverified: !row.freshness.assessmentVersionKnown,
  notAutoUpdatedReason: pageWide.has('notAutoUpdated') ? null : row.processing.blockedReason,
  updating: updatingLabel(row.processing),
  failed: isUpdateFailed(row.processing),
});
export type RowFlags = ReturnType<typeof visibleRowFlags>;
export const formatMatchingTime = (value: string | null) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Not yet';
export const recommendationEmptyMessage = (
  data: Recommendations<Recommendation> | undefined,
  filtered: boolean,
  projects: boolean
) => {
  if (!data) return 'No recommendations loaded.';
  if (filtered) return 'No recommendations match these filters.';
  const c = data.coverage;
  if (data.processingActive) return 'Recommendations are still being processed.';
  if (!data.processingEnabled || data.processingBlockedReason)
    return 'Processing is paused. No stored recommendations are available.';
  if (c.failed || c.blocked) return 'No stored recommendations are available. Some assessments failed or are blocked.';
  if (c.outstanding || c.undiscoveredPairs) return 'Recommendations are awaiting processing.';
  if (c.eligiblePairs > 0 && c.currentQuickRejected === c.eligiblePairs)
    return `No ${projects ? 'projects' : 'candidates'} passed the current matching gate.`;
  if (c.eligiblePairs === 0) return 'No pairs are currently eligible for automatic matching.';
  return 'No stored recommendations are available.';
};
